import React, { useState, useEffect } from 'react';
import { withFirebase } from '../../firebase';
import { ToastContainer, toast } from 'react-toastify';
import { getDatabase, ref, set, get, child } from "firebase/database";
import 'react-toastify/dist/ReactToastify.css';
import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from '../../../utils/EnvironmentSwitcher'; // Import config utility

const generateAPIKey = () => {
  const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const length = 32;
  let apiKey = '';
  for (let i = 0; i < length; i++) {
    apiKey += characters.charAt(Math.floor(Math.random() * characters.length));
  }
  return apiKey;
};

const APIKeyTable = withFirebase(({ fhirToken, authUser }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [newApiKey, setNewApiKey] = useState('');
  const [deviceDescription, setDeviceDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [claimKey, setClaimKey] = useState('');

  const fetchApiKeys = async () => {
    try {
      if (!fhirToken) {
        const fhirToken = localStorage.getItem('mieFToken');
      }
      if (!fhirToken || !authUser?.uid) {
        setError('Missing FHIR token or user ID.');
        return;
      }
      setIsLoading(true);
      
      // Get a fresh ID token
      const idToken = await authUser.getIdToken(true);
      
      const response = await axios.get(
        `${config.fhirUrl}/fhir/Device?identifier=https://fhir.mieros.cloud/fhir/ids|${authUser.uid}`,
        {
          headers: { Authorization: `Bearer ${idToken}` },
        }
      );

      setApiKeys(
        (response.data.entry || []).map((entry) => {
          const resource = entry.resource;
          return {
            id: resource.id,
            apiKey: resource.identifier?.find((id) => id.system === 'https://fhir.mieros.cloud/fhir/keys')?.value || 'N/A',
            description: resource.type?.coding[0]?.display || 'N/A',
            status: resource.status || 'unknown',
            lastUpdated: resource.meta?.lastUpdated || 'N/A',
          };
        })
      );
      setIsLoading(false);
    } catch (err) {
      console.error('Error fetching API keys:', err);
      setError('Failed to fetch API keys.');
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, [fhirToken, authUser]);

  const handleGenerateKey = () => {
    const generatedKey = generateAPIKey();
    setNewApiKey(generatedKey);
  };

  const handleAddKey = async () => {
    try {
      // Get a fresh ID token
      const idToken = await authUser.getIdToken(true);
      
      if (!idToken) {
        toast.error('Firebase token is missing. Please try again.');
        return;
      }

      const deviceId = generateAPIKey(); // Use a separate key for the Device ID
      const robotKey = uuidv4();
      await axios.post(
        `${config.fhirUrl}/fhir/Device`,
        {
          resourceType: 'Device',
          id: deviceId,
          identifier: [
            { system: 'https://fhir.mieros.cloud/fhir/ids', value: authUser.uid },
            { system: 'https://fhir.mieros.cloud/fhir/ids', value: deviceId },
            { system: 'https://fhir.mieros.cloud/fhir/keys', value: robotKey },
          ],
          meta: {
            profile: 'http://hl7.org/fhir/device/Device.profile.json',
          },
          type: {
            coding: [
              {
                system: 'http://snomed.info/sct',
                code: '309852001',
                display: deviceDescription || 'Ring MiE',
              },
            ],
          },
          manufacturer: {
            reference: 'Organization/mieros',
          },
          model: 'PX123',
          status: 'active',
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      toast.success('Device added successfully');
      setNewApiKey('');
      setDeviceDescription('');
      fetchApiKeys(); // Refresh API keys to include the newly added device
    } catch (error) {
      console.error('Error adding device:', error);
      toast.error('Failed to add device.');
    }
  };

  const handleClaimKey = async () => {
    try {
      // Get a fresh ID token
      const idToken = await authUser.getIdToken(true);
      
      if (!idToken) {
        toast.error('Firebase token is missing. Please try again.');
        return;
      }

      if (!claimKey) {
        toast.error('Please enter a device key to claim.');
        return;
      }

      const deviceId = claimKey;
      const robotKey = uuidv4();

      console.log('Claiming device with ID:', deviceId);
      console.log('User ID:', authUser.uid);
      console.log('Using FHIR URL:', config.fhirUrl);

      // Check if device already exists in FHIR
      try {
        const checkResponse = await axios.get(
          `${config.fhirUrl}/fhir/Device/${deviceId}`,
          {
            headers: { Authorization: `Bearer ${idToken}` },
          }
        );
        
        if (checkResponse.status === 200) {
          toast.error('Device already exists in FHIR.');
          return;
        }
      } catch (error) {
        // 404 is expected if device doesn't exist yet
        if (error.response?.status !== 404) {
          console.error('Error checking device existence:', error);
          toast.error('Error checking device existence.');
          return;
        }
      }

      // Add claim to firebase
      const db = getDatabase();
      const deviceRef = ref(db, 'claimedDevices/' + deviceId);
      const snapshot = await get(deviceRef);
      if (snapshot.exists()) {
        toast.error('Device has already been claimed.');
        return;
      }

      // Create device in FHIR
      const res = await axios.post(
        `${config.fhirUrl}/fhir/Device`,
        {
          resourceType: 'Device',
          id: deviceId,
          identifier: [
            { system: 'https://fhir.mieros.cloud/fhir/ids', value: authUser.uid },
            { system: 'https://fhir.mieros.cloud/fhir/keys', value: robotKey },
          ],
          meta: {
            profile: 'http://hl7.org/fhir/device/Device.profile.json',
          },
          type: {
            coding: [
              {
                system: 'https://fhir.mieros.cloud/fhir/devices',
                code: deviceId,
                display: deviceDescription || 'Device Claim',
              },
            ],
          },
          manufacturer: {
            reference: 'Organization/mieros',
          },
          model: 'PX123',
          status: 'active',
        },
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
          },
        }
      );

      console.log('Device created in FHIR:', res.data);

      // Log the claimed device to Firebase Realtime Database
      await set(deviceRef, {
        description: deviceDescription || 'Device Claim',
        uid: authUser.uid,
        dateClaimed: new Date().toISOString(),
      });

      toast.success('Device claimed successfully');
      setClaimKey('');
      setDeviceDescription('');
      fetchApiKeys(); // Refresh API keys to include the newly added device
    } catch (error) {
      console.error('Error claiming device:', error);
      toast.error(`Failed to claim device: ${error.message}`);
    }
  };

  const handleDeleteKey = async (index) => {
    try {
      const deviceToDelete = apiKeys[index];
      if (!deviceToDelete) {
        toast.error('Device not found.');
        return;
      }

      // Get a fresh ID token
      const idToken = await authUser.getIdToken(true);

      await axios.delete(`${config.fhirUrl}/fhir/Device/${deviceToDelete.id}`, {
        headers: { Authorization: `Bearer ${idToken}` },
      });

      fetchApiKeys(); // Refresh API keys after deletion
      toast.success('Device deleted successfully.');
    } catch (error) {
      console.error('Error deleting device:', error);
      toast.error('Failed to delete device.');
    }
  };

  if (isLoading) {
    return <p>Loading API keys...</p>;
  }

  if (error) {
    return <p className="text-danger">{error}</p>;
  }

  return (
    <div className="api-key-management">
      <ToastContainer />
      <h2>API Key Management</h2>
      
      <div className="form-group">
        <label htmlFor="deviceDescription">Device Description:</label>
        <input
          type="text"
          id="deviceDescription"
          value={deviceDescription}
          onChange={(e) => setDeviceDescription(e.target.value)}
        />
      </div>
      
      <div className="action-buttons">
        <button className="btn btn-primary" onClick={handleGenerateKey}>Generate API Key</button>
        <input
          type="text"
          value={newApiKey}
          onChange={(e) => setNewApiKey(e.target.value)}
        />
        <button className="btn btn-success" onClick={handleAddKey}>Add Key</button>
      </div>
      
      <div className="action-buttons">
        <button className="btn btn-primary" onClick={handleClaimKey}>Device Claim Form</button>
        <input
          type="text"
          value={claimKey}
          onChange={(e) => setClaimKey(e.target.value)}
        />
        <button className="btn btn-success" onClick={handleClaimKey}>Add Key</button>
      </div>
      
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th>API Key</th>
            <th>Description</th>
            <th>Status</th>
            <th>Last Updated</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {apiKeys.map((apiKey, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{apiKey.apiKey}</td>
              <td>{apiKey.description}</td>
              <td>{apiKey.status}</td>
              <td>{new Date(apiKey.lastUpdated).toLocaleString()}</td>
              <td>
                <button onClick={() => handleDeleteKey(index)}>Delete</button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
});

export default APIKeyTable;
